import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  ProgressBar,
  Cards,
  Link,
  PieChart,
  Alert,
  Table
} from '@cloudscape-design/components';

// Dummy data for the overview
const analysisMetrics = {
  totalFiles: 17,
  analyzedFiles: 17,
  confidenceScore: 94,
  classificationBreakdown: [
    { title: 'Entity Classes', value: 6, color: '#688ee0' },
    { title: 'Service Classes', value: 8, color: '#9dc3ff' },
    { title: 'Data Access', value: 3, color: '#f2b860' },
    { title: 'Utility Classes', value: 1, color: '#6aad87' }
  ],
  criticalFindings: 8,
  majorFindings: 15,
  minorFindings: 23
};

export function OverviewTab({ project }){
  return (
    <SpaceBetween size="l">
      {/* Analysis Status Summary */}
      <Container
        header={
          <Header
            variant="h2"
            description="Current status of technical analysis"
          >
            Analysis Status
          </Header>
        }
      >
        <ColumnLayout columns={3} variant="text-grid">
          <Box>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Analysis Progress</Box>
              <ProgressBar
                value={100}
                label={`${analysisMetrics.analyzedFiles}/${analysisMetrics.totalFiles} files analyzed`}
                maxValue={analysisMetrics.totalFiles}
              />
            </SpaceBetween>
          </Box>
          <Box>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Overall Confidence Score</Box>
              <Box variant="h1" color="text-status-success">
                {analysisMetrics.confidenceScore}%
              </Box>
            </SpaceBetween>
          </Box>
          <Box>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Findings</Box>
              <SpaceBetween size="xxs">
                <StatusIndicator type="error">
                  {analysisMetrics.criticalFindings} Critical
                </StatusIndicator>
                <StatusIndicator type="warning">
                  {analysisMetrics.majorFindings} Major
                </StatusIndicator>
                <StatusIndicator type="info">
                  {analysisMetrics.minorFindings} Minor
                </StatusIndicator>
              </SpaceBetween>
            </SpaceBetween>
          </Box>
        </ColumnLayout>
      </Container>

      {/* Key Insights */}
      <Container
        header={
          <Header
            variant="h2"
            description="Important findings from the analysis"
          >
            Key Insights
          </Header>
        }
      >
        <ColumnLayout columns={2}>
          {/* Code Classification */}
          <Container
            header={
              <Header
                variant="h3"
                actions={<Link>View details</Link>}
              >
                Code Classification
              </Header>
            }
          >
            <PieChart
              data={analysisMetrics.classificationBreakdown}
              detailPopoverContent={(datum, sum) => [
                { key: 'Classes', value: datum.value },
                { key: 'Percentage', value: `${((datum.value / sum) * 100).toFixed(1)}%` }
              ]}
              size="medium"
              variant="donut"
              innerMetricDescription="Total Classes"
              innerMetricValue={analysisMetrics.classificationBreakdown.reduce((sum, item) => sum + item.value, 0)}
            />
          </Container>

          {/* High Priority Findings */}
          <Container
            header={
              <Header
                variant="h3"
                actions={<Link>View all findings</Link>}
              >
                High Priority Findings
              </Header>
            }
          >
            <SpaceBetween size="m">
              <Alert type="warning">
                Found {analysisMetrics.criticalFindings} critical issues that require immediate attention
              </Alert>
              <Table
                columnDefinitions={[
                  {
                    id: 'finding',
                    header: 'Finding',
                    cell: item => item.description
                  },
                  {
                    id: 'severity',
                    header: 'Severity',
                    cell: item => (
                      <StatusIndicator type={item.severityType}>
                        {item.severity}
                      </StatusIndicator>
                    )
                  }
                ]}
                items={[
                  {
                    description: 'Tightly coupled components in OrderService',
                    severity: 'Critical',
                    severityType: 'error'
                  },
                  {
                    description: 'Circular dependency detected',
                    severity: 'Critical',
                    severityType: 'error'
                  },
                  {
                    description: 'Database connection pooling issues',
                    severity: 'Major',
                    severityType: 'warning'
                  }
                ]}
                variant="embedded"
                stripedRows
              />
            </SpaceBetween>
          </Container>
        </ColumnLayout>
      </Container>

      {/* Quick Actions */}
      <Cards
        cardDefinition={{
          header: item => (
            <Header variant="h3">{item.title}</Header>
          ),
          sections: [
            {
              id: 'description',
              content: item => item.description
            },
            {
              id: 'link',
              content: item => (
                <Link href={item.href}>
                  {item.linkText}
                </Link>
              )
            }
          ]
        }}
        items={[
          {
            title: 'Code Analysis',
            description: 'Review detailed analysis of code structure and patterns',
            linkText: 'View code analysis',
            href: '#'
          },
          {
            title: 'Dependencies',
            description: 'Explore internal and external dependencies',
            linkText: 'View dependencies',
            href: '#'
          },
          {
            title: 'Data Relationships',
            description: 'Review identified data models and relationships',
            linkText: 'View relationships',
            href: '#'
          },
          {
            title: 'Operation Flows',
            description: 'Analyze business operations and transaction flows',
            linkText: 'View flows',
            href: '#'
          }
        ]}
        cardsPerRow={[
          { cards: 1 },
          { minWidth: 500, cards: 2 }
        ]}
      />

      {/* Recommendations */}
      <Container
        header={
          <Header
            variant="h2"
            description="AI-generated recommendations based on analysis"
          >
            Recommendations
          </Header>
        }
      >
        <Table
          columnDefinitions={[
            {
              id: 'recommendation',
              header: 'Recommendation',
              cell: item => item.description
            },
            {
              id: 'impact',
              header: 'Impact',
              cell: item => (
                <StatusIndicator type={item.impactType}>
                  {item.impact}
                </StatusIndicator>
              )
            },
            {
              id: 'confidence',
              header: 'AI Confidence',
              cell: item => `${item.confidence}%`
            }
          ]}
          items={[
            {
              description: 'Split OrderService into separate bounded contexts',
              impact: 'High',
              impactType: 'error',
              confidence: 92
            },
            {
              description: 'Implement CQRS pattern for order processing',
              impact: 'Medium',
              impactType: 'warning',
              confidence: 87
            },
            {
              description: 'Refactor data access layer to use repository pattern',
              impact: 'Medium',
              impactType: 'warning',
              confidence: 89
            }
          ]}
          variant="embedded"
          stripedRows
        />
      </Container>
    </SpaceBetween>
  );
};

